import React, {useCallback, useMemo} from 'react';
import {Button, Card, CardContent, CardHeader, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PublicIcon from '@mui/icons-material/Public';
import {GlobalKeyVault, Office, PermanentToken} from "../../api/Model";
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {useRemoteValue} from "../../util/RemoteValue";
import {useOfficeStore} from "../../api/OfficeStore";
import {useGlobalKeyVaultStore} from "../../api/GlobalKeyVaultStore";
import {OfficeIcon} from "../../components/office-icon/OfficeIcon";
import {KeyVaultChip} from "../../components/key-vault-chip/KeyVaultChip";
import {useCopyDialog} from "../../components/copy-dialog/CopyDialog";
import {PermanentTokens_Admin} from "../../util/access/Permission";
import {usePermission} from "../../util/access/UseAccess";
import {usePermanentTokenStore} from "../../api/PermanentTokenStore";
import {PermanentTokenDialog} from "./PermanentTokenDialog";
import {PermissionList} from "./PermissionPresentation";

function PermanentTokenCard({token, offices, keyVaults, onEdit, onDelete, canAdmin}: {
    token: PermanentToken;
    offices: Office[];
    keyVaults: GlobalKeyVault[];
    onEdit: (row: PermanentToken) => void;
    onDelete: (row: PermanentToken) => void;
    canAdmin: boolean;
}) {
    const {officeDisplayName, officeIcon, keyVault} = useMemo(() => {
        if (token.officeId) {
            return {
                officeDisplayName: offices.find(office => office.id === token.officeId)?.name || 'n/a',
                officeIcon: <OfficeIcon officeId={token.officeId} colored fontSize="large"/>,
                keyVault: token.vaultId ? keyVaults.find(vault => vault.id === token.vaultId) : undefined,
            }
        } else {
            return {
                officeDisplayName: 'All Offices',
                officeIcon: <PublicIcon color="secondary" fontSize="large"/>,
                keyVault: undefined,
            }
        }
    }, [token.officeId, token.vaultId, offices, keyVaults]);

    const handleEdit = useCallback(() => {
        onEdit(token);
    }, [onEdit, token]);

    const handleDelete = useCallback(() => {
        onDelete(token);
    }, [onDelete, token]);

    return (
        <Card variant="outlined">
            <CardHeader
                avatar={officeIcon}
                title={
                    <Stack direction="row" alignItems="baseline" gap={1}>
                        <Typography variant="h6">{token.name}</Typography>
                        {token.description && (
                            <Typography variant="body2" color="text.secondary">{token.description}</Typography>
                        )}
                    </Stack>
                }
                subheader={
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="body1">Scope: {officeDisplayName}</Typography>
                        {keyVault && (
                            <KeyVaultChip keyVault={keyVault} size="small"/>
                        )}
                    </Stack>
                }
                action={canAdmin && (
                    <Stack direction="row">
                        <Tooltip title="Edit">
                            <IconButton
                                color="primary"
                                onClick={handleEdit}
                            >
                                <EditIcon/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton
                                color="error"
                                onClick={handleDelete}
                            >
                                <DeleteIcon/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            />
            <CardContent>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    {token.permissions.length > 0 ? (
                        <PermissionList permissions={token.permissions}/>
                    ) : (
                        <Typography variant="body2" color="text.secondary">No permissions</Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function PermanentTokenPage() {
    const {
        remoteValue: permanentTokens,
        createPermanentToken,
        updatePermanentToken,
        deletePermanentToken,
    } = usePermanentTokenStore();

    const officeStore = useOfficeStore();
    const offices = useRemoteValue(officeStore.remoteValue, []);

    const keyVaultStore = useGlobalKeyVaultStore();
    const keyVaults = useRemoteValue(keyVaultStore.remoteValue, []);
    const {canAdmin} = usePermission({canAdmin: PermanentTokens_Admin});

    const showTokenValue = useCopyDialog({
        title: 'Permanent Token',
        description: 'Copy the permanent token value below. After you close this dialog, there will be no way to retrieve the token again.',
    });

    const handleCreatePermanentToken = useFormDialogAction({
        title: 'Create Permanent Token',
        description: 'Enter the details for the new permanent token.',
        submitButtonText: 'Create',
        renderForm: PermanentTokenDialog,
        action: (openDialog) => openDialog({
            initialValue: {
                name: '',
                description: null,
                officeId: null,
                vaultId: null,
                permissions: [],
            },
            onSubmit: async (form) => {
                const newPermanentToken = await createPermanentToken(form);
                await showTokenValue(newPermanentToken.token);
                return newPermanentToken;
            },
            successMessage: form => `Created new permanent token ${form.name}`,
            errorMessage: 'Failed to create new permanent token. Please try again later.',
        })
    }, [createPermanentToken, showTokenValue]);

    const handleUpdatePermanentToken = useFormDialogAction({
        title: 'Edit Permanent Token',
        description: 'Update the permanent token information below.',
        submitButtonText: 'Update',
        renderForm: PermanentTokenDialog,
        action: (openDialog, token: PermanentToken) => openDialog({
            initialValue: {
                name: token.name,
                description: token.description,
                officeId: token.officeId,
                vaultId: token.vaultId,
                permissions: [...token.permissions],
            },
            onSubmit: form => updatePermanentToken(token, form),
            successMessage: form => `Updated permanent token ${form.name}`,
            errorMessage: 'Failed to update permanent token. Please try again later.',
        })
    }, [updatePermanentToken]);

    const handleDeletePermanentToken = useDeleteAction({
        description: 'Are you sure you want to delete this permanent token? This action cannot be undone.',
        action: deletePermanentToken,
        successMessage: token => `Deleted permanent token ${token.name}`,
        failureMessage: token => `Failed to delete permanent token ${token.name}`,
    }, [deletePermanentToken]);

    return (
        <Page
            header="Permanent Tokens"
            counter={<StorePageCount value={permanentTokens}/>}
            description="Manage permanent tokens. The permanent token is a token that is used by other applications to access the system without the need for a user to log in."
            actions={canAdmin && (
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon/>}
                    onClick={handleCreatePermanentToken}
                >
                    Add Permanent Token
                </Button>
            )}>

            <LoadingData
                value={permanentTokens}
                emptyMessage="No permanent tokens found."
                defaultErrorMessage="Failed to load permanent tokens. Please try again later."
                children={permanentTokens => (
                    <Stack spacing={2} direction="column">
                        {permanentTokens.map(token => (
                            <PermanentTokenCard
                                key={token.id}
                                token={token}
                                offices={offices}
                                keyVaults={keyVaults}
                                onEdit={handleUpdatePermanentToken}
                                onDelete={handleDeletePermanentToken}
                                canAdmin={canAdmin}
                            />
                        ))}
                    </Stack>
                )}
            />
        </Page>
    );
};
